const PENDING_KEY = 'labop.hosted-mcp.pending-login';
const REQUEST_ID = /^[A-Za-z0-9_-]{43}$/;
const RETURN_LIFETIME_MS = 10 * 60 * 1000;

export function hostedRequestId(search) {
  const values = new URLSearchParams(search).getAll('request');
  return values.length === 1 && REQUEST_ID.test(values[0]) ? values[0] : null;
}

export function rememberHostedLogin(requestId, now = Date.now()) {
  if (!REQUEST_ID.test(requestId || '')) return false;
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ requestId, expiresAt: now + RETURN_LIFETIME_MS }));
    return true;
  } catch { return false; }
}

export function clearHostedLogin() {
  try { sessionStorage.removeItem(PENDING_KEY); } catch { /* Storage can be disabled by the browser. */ }
}

export function hostedLoginReturnPath(now = Date.now()) {
  try {
    const pending = JSON.parse(sessionStorage.getItem(PENDING_KEY));
    if (REQUEST_ID.test(pending?.requestId || '') && Number.isFinite(pending.expiresAt)
        && pending.expiresAt > now && pending.expiresAt <= now + RETURN_LIFETIME_MS) {
      return `/connect/hosted?request=${pending.requestId}`;
    }
  } catch { /* Invalid or unavailable storage does not change the normal login route. */ }
  clearHostedLogin();
  return '/';
}

function callbackUrl(value) {
  if (typeof value !== 'string' || /[\u0000-\u0020\u007f]/.test(value)) throw new Error('Invalid callback');
  const url = new URL(value);
  const loopback = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
  if (url.username || url.password || url.hash
      || !(url.protocol === 'https:' || (url.protocol === 'http:' && loopback && url.port))) {
    throw new Error('Invalid callback');
  }
  return url;
}

// The server validates the registered destination; the browser additionally
// refuses changed destinations or non-web schemes in the decision response.
export function approvedCallback(redirect, registeredCallback) {
  try {
    const registered = callbackUrl(registeredCallback);
    const result = callbackUrl(redirect);
    if (registered.search || result.origin !== registered.origin || result.pathname !== registered.pathname) return null;
    const keys = [...result.searchParams.keys()];
    if (keys.some(key => !['code', 'state', 'iss', 'error', 'error_description'].includes(key))
        || keys.some((key, index) => keys.indexOf(key) !== index)
        || Boolean(result.searchParams.get('code')) === Boolean(result.searchParams.get('error'))) return null;
    return result.href;
  } catch { return null; }
}

export function hostedEndpointUrl(value, identityId) {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password && !url.search && !url.hash
      && url.pathname === `/api/mcp/${identityId}` ? url.href : null;
  } catch { return null; }
}

export async function hostedAdminRequest(path, body) {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Sign in as a human administrator to continue.');
  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/hosted-mcp${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { Authorization: `Bearer ${token}`, ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
    cache: 'no-store',
    credentials: 'omit',
    redirect: 'error',
    referrerPolicy: 'no-referrer',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    if (response.status === 401) window.dispatchEvent(new Event('auth-expired'));
    const message = response.status === 401 ? 'Your sign-in expired. Sign in again to continue.'
      : response.status === 403 ? 'A human administrator with the required access must review this connection.'
      : response.status === 409 ? 'The connection details changed or this request was already used. Refresh the details and review them again.'
      : [404, 410].includes(response.status) ? 'This connection request is unavailable or expired. Start connecting again from your client.'
      : response.status === 503 ? 'Hosted connections are unavailable. Ask your administrator to check the hosted connection settings.'
      : 'Could not complete this request. Refresh and try again.';
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  try { return await response.json(); }
  catch { throw new Error('Could not read the connection details. Refresh and try again.'); }
}
